import React, { useRef, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../utils/api';
import { analyzeAudio } from '../utils/audioAnalyzer';

/**
 * Waveform Component - Professional DJ waveform visualization
 * 
 * Features:
 * - Uses pre-computed waveform from database when available (instant load!)
 * - Falls back to client-side generation if needed
 * - Color-coded frequency bands (bass/mid/treble)
 * - Beat grid overlay (based on BPM)
 * - Current position indicator
 * - Click-to-seek functionality
 * - Responsive canvas rendering
 * 
 * @param {Object} props
 * @param {string} props.trackId - Track ID for fetching waveform
 * @param {string} props.audioUrl - Direct audio URL for client-side analysis
 * @param {Object} props.preloadedWaveform - Pre-computed waveform data from database
 * @param {number} props.currentTime - Current playback position (seconds)
 * @param {number} props.duration - Total track duration (seconds)
 * @param {number} props.bpm - Track BPM for beat grid
 * @param {Function} props.onSeek - Callback when user clicks to seek (position in seconds)
 * @param {Function} props.onAnalysisComplete - Callback when client analysis completes
 * @param {Function} props.onWaveformGenerated - Callback to save waveform to database
 * @param {number} props.height - Canvas height in pixels (default 80)
 * @param {string} props.className - Additional CSS classes
 */
const Waveform = ({ 
  trackId, 
  audioUrl,
  preloadedWaveform = null,
  currentTime = 0, 
  duration = 0,
  bpm = null,
  onSeek,
  onAnalysisComplete,
  onWaveformGenerated,
  height = 80,
  className = ''
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [waveformData, setWaveformData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hoveredTime, setHoveredTime] = useState(null);
  const [analysisMethod, setAnalysisMethod] = useState(null);

  // Fetch or generate waveform data when trackId/audioUrl changes
  useEffect(() => {
    if (!trackId && !audioUrl && !preloadedWaveform) {
      setWaveformData(null);
      return;
    }

    const fetchOrGenerateWaveform = async () => {
      // OPTIMIZATION: Use pre-loaded waveform from database if available (instant!)
      if (preloadedWaveform && Array.isArray(preloadedWaveform) && preloadedWaveform.length > 0) {
        console.log('⚡ Using pre-loaded waveform from database (instant!)');
        const waveformFormatted = {
          data: preloadedWaveform.map((amplitude, index) => ({
            time: (index / preloadedWaveform.length) * (duration || 180),
            amplitude: amplitude,
            freq_low: amplitude * 0.6,
            freq_mid: amplitude * 0.3,
            freq_high: amplitude * 0.1
          })),
          duration: duration || 180,
          bpm: bpm
        };
        setWaveformData(waveformFormatted);
        setAnalysisMethod('preloaded');
        return;
      }

      setLoading(true);
      setError(null);

      // Try client-side analysis if audioUrl is available
      if (audioUrl) {
        try {
          console.log('🎵 Generating waveform client-side...');
          const result = await analyzeAudio(audioUrl, 500);
          
          // Convert to expected format
          const waveformFormatted = {
            data: result.waveform.map((amplitude, index) => ({
              time: (index / result.waveform.length) * result.duration,
              amplitude: amplitude,
              freq_low: amplitude * 0.6,
              freq_mid: amplitude * 0.3,
              freq_high: amplitude * 0.1
            })),
            duration: result.duration,
            bpm: result.bpm
          };
          
          setWaveformData(waveformFormatted);
          setAnalysisMethod('client');
          console.log('✅ Client-side waveform generated');
          
          // Notify parent to save waveform to database for future use
          if (onWaveformGenerated && trackId) {
            onWaveformGenerated(trackId, {
              waveform: result.waveform,
              bpm: result.bpm,
              duration: result.duration
            });
          }
          
          // Notify parent of analysis results
          if (onAnalysisComplete) {
            onAnalysisComplete({
              bpm: result.bpm,
              duration: result.duration
            });
          }
          
          setLoading(false);
          return;
        } catch (clientErr) {
          console.warn('Client-side analysis failed, trying server:', clientErr);
        }
      }

      // Fallback to server-side (may timeout for R2 files)
      if (trackId) {
        try {
          const response = await api.get(`/tracks/${trackId}/waveform?samples=500`);
          
          // Check if server returned demo/placeholder data
          if (response.data.is_demo) {
            // Generate simple placeholder waveform
            const placeholderData = {
              data: Array(500).fill(null).map((_, i) => ({
                time: (i / 500) * (duration || 180),
                amplitude: 0.3 + Math.random() * 0.2,
                freq_low: 0.2,
                freq_mid: 0.3,
                freq_high: 0.1
              })),
              duration: duration || 180
            };
            setWaveformData(placeholderData);
            setAnalysisMethod('placeholder');
          } else {
            setWaveformData(response.data);
            setAnalysisMethod('server');
          }
        } catch (err) {
          console.error('Waveform fetch error:', err);
          
          // Generate placeholder on error
          const placeholderData = {
            data: Array(500).fill(null).map((_, i) => ({
              time: (i / 500) * (duration || 180),
              amplitude: 0.3 + Math.random() * 0.2,
              freq_low: 0.2,
              freq_mid: 0.3,
              freq_high: 0.1
            })),
            duration: duration || 180
          };
          setWaveformData(placeholderData);
          setAnalysisMethod('placeholder');
        }
      }
      
      setLoading(false);
    };

    fetchOrGenerateWaveform();
  }, [trackId, audioUrl]);

  // Draw waveform on canvas (optimized: throttled re-renders)
  useEffect(() => {
    if (!waveformData || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const container = containerRef.current;

    // Set canvas size to match container
    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = height;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw waveform
    drawWaveform(ctx, waveformData, canvas.width, canvas.height, currentTime, duration, bpm, hoveredTime);
  }, [waveformData, Math.floor(currentTime * 2) / 2, duration, bpm, height, hoveredTime]); // Throttle currentTime updates to 0.5s

  // Handle canvas click for seek
  const handleCanvasClick = (e) => {
    if (!onSeek || !duration || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const clickPosition = (x / rect.width) * duration;

    onSeek(clickPosition);
  };

  // Handle mouse move for hover preview
  const handleMouseMove = (e) => {
    if (!duration || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const hoverTime = (x / rect.width) * duration;

    setHoveredTime(hoverTime);
  };

  const handleMouseLeave = () => {
    setHoveredTime(null);
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-dark-input/30 rounded-lg ${className}`} style={{ height: `${height}px` }}>
        <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
        <span className="ml-2 text-sm text-sunset-orange">Chargement waveform...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-dark-input/30 rounded-lg ${className}`} style={{ height: `${height}px` }}>
        <span className="text-xs text-red-400">Erreur: {error}</span>
      </div>
    );
  }

  if (!waveformData) {
    return (
      <div className={`flex items-center justify-center bg-dark-input/30 rounded-lg ${className}`} style={{ height: `${height}px` }}>
        <span className="text-xs text-sunset-orange/50">Aucune waveform</span>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`relative bg-dark-input/30 rounded-lg overflow-hidden cursor-pointer ${className}`}
      style={{ height: `${height}px` }}
      onClick={handleCanvasClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      title="Cliquez pour naviguer dans le track"
    >
      <canvas 
        ref={canvasRef}
        className="w-full h-full"
      />
    </div>
  );
};

/**
 * Draw waveform on canvas with modern gradient style (Serato/Rekordbox inspired)
 */
function drawWaveform(ctx, waveformData, width, height, currentTime, duration, bpm, hoveredTime) {
  const data = waveformData.data;
  if (!data || data.length === 0) return;

  const centerY = height / 2;
  const barWidth = width / data.length;

  // ═══════════════════════════════════════════════════════════════
  // BACKGROUND GRADIENT (subtle dark glow)
  // ═══════════════════════════════════════════════════════════════
  const bgGradient = ctx.createLinearGradient(0, 0, 0, height);
  bgGradient.addColorStop(0, 'rgba(15, 23, 42, 0.8)');
  bgGradient.addColorStop(0.5, 'rgba(30, 41, 59, 0.6)');
  bgGradient.addColorStop(1, 'rgba(15, 23, 42, 0.8)');
  ctx.fillStyle = bgGradient;
  ctx.fillRect(0, 0, width, height);

  // ═══════════════════════════════════════════════════════════════
  // DRAW WAVEFORM BARS (Modern gradient with glow effect)
  // ═══════════════════════════════════════════════════════════════
  data.forEach((point, index) => {
    const x = index * barWidth;
    const amplitude = point.amplitude || 0;
    const barHeight = amplitude * (height * 0.85); // 85% of canvas height

    // Determine dominant frequency for color
    const freqLow = point.freq_low || 0;
    const freqMid = point.freq_mid || 0;
    const freqHigh = point.freq_high || 0;

    // Check if this bar is in the played section
    const pointTime = point.time;
    const isPlayed = pointTime <= currentTime;

    // Modern color scheme with gradient
    let gradient;
    if (isPlayed) {
      // PLAYED SECTION: Vibrant gradient with frequency-based colors
      gradient = ctx.createLinearGradient(0, centerY - barHeight / 2, 0, centerY + barHeight / 2);
      
      if (freqLow > freqMid && freqLow > freqHigh) {
        // Bass dominant - Electric Blue gradient
        gradient.addColorStop(0, `rgba(56, 189, 248, ${0.9 + amplitude * 0.1})`);
        gradient.addColorStop(0.5, `rgba(14, 165, 233, ${0.95})`);
        gradient.addColorStop(1, `rgba(2, 132, 199, ${0.9 + amplitude * 0.1})`);
        ctx.shadowColor = 'rgba(56, 189, 248, 0.6)';
      } else if (freqMid > freqLow && freqMid > freqHigh) {
        // Mid dominant - Warm Orange/Yellow gradient
        gradient.addColorStop(0, `rgba(251, 146, 60, ${0.9 + amplitude * 0.1})`);
        gradient.addColorStop(0.5, `rgba(249, 115, 22, ${0.95})`);
        gradient.addColorStop(1, `rgba(234, 88, 12, ${0.9 + amplitude * 0.1})`);
        ctx.shadowColor = 'rgba(251, 146, 60, 0.6)';
      } else if (freqHigh > 0) {
        // Treble dominant - Hot Pink/Magenta gradient
        gradient.addColorStop(0, `rgba(244, 114, 182, ${0.9 + amplitude * 0.1})`);
        gradient.addColorStop(0.5, `rgba(236, 72, 153, ${0.95})`);
        gradient.addColorStop(1, `rgba(219, 39, 119, ${0.9 + amplitude * 0.1})`);
        ctx.shadowColor = 'rgba(244, 114, 182, 0.6)';
      } else {
        // Default - Purple/Violet gradient
        gradient.addColorStop(0, `rgba(196, 181, 253, ${0.9 + amplitude * 0.1})`);
        gradient.addColorStop(0.5, `rgba(168, 85, 247, ${0.95})`);
        gradient.addColorStop(1, `rgba(147, 51, 234, ${0.9 + amplitude * 0.1})`);
        ctx.shadowColor = 'rgba(168, 85, 247, 0.6)';
      }
      
      // Add glow effect for played section
      ctx.shadowBlur = 8 + amplitude * 4;
    } else {
      // UNPLAYED SECTION: Subtle gray gradient (dimmed)
      gradient = ctx.createLinearGradient(0, centerY - barHeight / 2, 0, centerY + barHeight / 2);
      gradient.addColorStop(0, 'rgba(71, 85, 105, 0.4)');
      gradient.addColorStop(0.5, 'rgba(51, 65, 85, 0.5)');
      gradient.addColorStop(1, 'rgba(71, 85, 105, 0.4)');
      ctx.shadowBlur = 0;
    }

    ctx.fillStyle = gradient;
    
    // Draw bar with rounded caps (modern look)
    const actualBarWidth = Math.max(barWidth * 0.8, 1.5); // Slight gap between bars
    const barX = x + (barWidth - actualBarWidth) / 2;
    
    // Top half
    ctx.beginPath();
    ctx.roundRect(barX, centerY - barHeight / 2, actualBarWidth, barHeight / 2, [2, 2, 0, 0]);
    ctx.fill();
    
    // Bottom half
    ctx.beginPath();
    ctx.roundRect(barX, centerY, actualBarWidth, barHeight / 2, [0, 0, 2, 2]);
    ctx.fill();
    
    // Reset shadow
    ctx.shadowBlur = 0;
  });

  // ═══════════════════════════════════════════════════════════════
  // DRAW BEAT GRID (Modern style with subtle glow)
  // ═══════════════════════════════════════════════════════════════
  if (bpm && bpm > 0 && duration > 0) {
    const beatDuration = 60 / bpm; // seconds per beat
    const beatsPerBar = 4; // 4/4 time signature
    const barDuration = beatDuration * beatsPerBar;

    // Draw bar lines (every 4 beats) - Brighter with glow
    ctx.shadowColor = 'rgba(168, 85, 247, 0.5)';
    ctx.shadowBlur = 3;
    ctx.strokeStyle = 'rgba(168, 85, 247, 0.4)';
    ctx.lineWidth = 1.5;

    for (let time = 0; time <= duration; time += barDuration) {
      const x = (time / duration) * width;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }

    // Draw beat lines (subtle)
    ctx.shadowBlur = 0;
    ctx.strokeStyle = 'rgba(168, 85, 247, 0.2)';
    ctx.lineWidth = 1;
    for (let time = 0; time <= duration; time += beatDuration) {
      const x = (time / duration) * width;
      // Skip if it's a bar line
      if (Math.abs(time % barDuration) > 0.01) {
        ctx.beginPath();
        ctx.moveTo(x, height * 0.3);
        ctx.lineTo(x, height * 0.7);
        ctx.stroke();
      }
    }
    
    ctx.shadowBlur = 0;
  }

  // ═══════════════════════════════════════════════════════════════
  // DRAW CURRENT POSITION INDICATOR (Modern with strong glow)
  // ═══════════════════════════════════════════════════════════════
  if (duration > 0 && currentTime >= 0) {
    const positionX = (currentTime / duration) * width;

    // Strong glow effect for position line
    ctx.shadowColor = 'rgba(255, 255, 255, 1)';
    ctx.shadowBlur = 15;

    // Position line (thicker, brighter)
    ctx.strokeStyle = 'rgba(255, 255, 255, 1)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(positionX, 0);
    ctx.lineTo(positionX, height);
    ctx.stroke();

    // Position indicator - Modern diamond shape
    ctx.shadowBlur = 20;
    ctx.fillStyle = 'rgba(255, 255, 255, 1)';
    
    // Top diamond (playhead)
    ctx.beginPath();
    ctx.moveTo(positionX, 0);
    ctx.lineTo(positionX - 6, 8);
    ctx.lineTo(positionX, 12);
    ctx.lineTo(positionX + 6, 8);
    ctx.closePath();
    ctx.fill();
    
    // Bottom diamond
    ctx.beginPath();
    ctx.moveTo(positionX, height);
    ctx.lineTo(positionX - 6, height - 8);
    ctx.lineTo(positionX, height - 12);
    ctx.lineTo(positionX + 6, height - 8);
    ctx.closePath();
    ctx.fill();

    // Center dot for extra visibility
    ctx.shadowBlur = 10;
    ctx.beginPath();
    ctx.arc(positionX, height / 2, 5, 0, Math.PI * 2);
    ctx.fill();

    // Reset shadow
    ctx.shadowBlur = 0;
  }

  // ═══════════════════════════════════════════════════════════════
  // DRAW HOVER PREVIEW (Modern tooltip with glow)
  // ═══════════════════════════════════════════════════════════════
  if (hoveredTime !== null && duration > 0) {
    const hoverX = (hoveredTime / duration) * width;

    // Hover line with glow
    ctx.shadowColor = 'rgba(168, 85, 247, 0.8)';
    ctx.shadowBlur = 10;
    ctx.strokeStyle = 'rgba(168, 85, 247, 0.6)';
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(hoverX, 0);
    ctx.lineTo(hoverX, height);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.shadowBlur = 0;

    // Modern tooltip with gradient background
    const minutes = Math.floor(hoveredTime / 60);
    const seconds = Math.floor(hoveredTime % 60);
    const timeText = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    ctx.font = 'bold 11px system-ui, -apple-system, sans-serif';
    const textWidth = ctx.measureText(timeText).width;
    const tooltipWidth = textWidth + 12;
    const tooltipHeight = 20;
    const tooltipX = Math.min(Math.max(hoverX - tooltipWidth / 2, 0), width - tooltipWidth);
    
    // Tooltip shadow
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 2;
    
    // Tooltip background with gradient
    const tooltipGradient = ctx.createLinearGradient(tooltipX, 5, tooltipX, 5 + tooltipHeight);
    tooltipGradient.addColorStop(0, 'rgba(168, 85, 247, 0.95)');
    tooltipGradient.addColorStop(1, 'rgba(147, 51, 234, 0.95)');
    ctx.fillStyle = tooltipGradient;
    ctx.beginPath();
    ctx.roundRect(tooltipX, 5, tooltipWidth, tooltipHeight, 6);
    ctx.fill();
    
    // Tooltip border glow
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
    ctx.strokeStyle = 'rgba(196, 181, 253, 0.5)';
    ctx.lineWidth = 1;
    ctx.stroke();
    
    // Tooltip text
    ctx.fillStyle = 'rgba(255, 255, 255, 1)';
    ctx.fillText(timeText, tooltipX + 6, 5 + tooltipHeight / 2 + 4);
  }
}

export default Waveform;
